<template>
  <el-dialog v-model="visible" :title="`门店排班 - ${store?.name || ''}`" width="920px">
    <div class="mb-12px flex flex-wrap items-center gap-12px">
      <el-date-picker
        v-model="weekStart"
        type="date"
        value-format="YYYY-MM-DD"
        @change="loadSchedule"
      />
      <el-button @click="applyWorkWeek">工作日模板</el-button>
      <el-button @click="copyNextWeek">复制到下一周</el-button>
      <el-button type="warning" @click="setDayOff">所选日休息</el-button>
      <el-button type="primary" plain @click="openAddSlot">添加时段</el-button>
    </div>
    <div class="mb-12px flex flex-wrap items-center gap-8px">
      <span class="text-13px text-gray-500">当前时段方案：</span>
      <el-tag
        v-for="slot in slotTemplates"
        :key="slot"
        closable
        @close="removeSlotTemplate(slot)"
      >
        {{ slot }}
      </el-tag>
      <span v-if="!slotTemplates.length" class="text-13px text-gray-400">暂无时段，请先添加</span>
    </div>
    <el-alert
      class="mb-12px"
      :closable="false"
      show-icon
      type="info"
      title="可自定义任意 HH:mm-HH:mm 时段。同一天开放时段不可重叠；用户端到店只会看到已「开放」且有容量的时段。"
    />
    <el-table :data="rows" border max-height="500px">
      <el-table-column label="日期" width="130">
        <template #default="{ row }">
          <el-radio v-model="selectedDate" :value="row.workDate">{{ row.workDate }}</el-radio>
        </template>
      </el-table-column>
      <el-table-column label="时段" prop="timeSlot" width="160" />
      <el-table-column label="容量" width="160">
        <template #default="{ row }">
          <el-input-number v-model="row.capacity" :min="row.booked" :max="50" />
        </template>
      </el-table-column>
      <el-table-column label="已约" prop="booked" width="90" />
      <el-table-column label="开放">
        <template #default="{ row }"><el-switch v-model="row.open" /></template>
      </el-table-column>
    </el-table>
    <el-empty
      v-if="!rows.some((row) => row.open)"
      description="本周暂无开放排班，可添加时段后打开「开放」开关"
    />
    <template #footer>
      <el-button @click="visible = false">取消</el-button>
      <el-button type="primary" :loading="saving" @click="save">保存</el-button>
    </template>
  </el-dialog>

  <el-dialog v-model="addSlotVisible" title="添加自定义时段" width="420px" append-to-body>
    <el-form label-width="88px">
      <el-form-item label="开始时间" required>
        <el-time-select
          v-model="addSlotForm.start"
          class="!w-1/1"
          start="00:00"
          step="00:30"
          end="23:30"
          placeholder="开始"
        />
      </el-form-item>
      <el-form-item label="结束时间" required>
        <el-time-select
          v-model="addSlotForm.end"
          class="!w-1/1"
          start="00:30"
          step="00:30"
          end="23:30"
          placeholder="结束"
        />
      </el-form-item>
      <el-form-item label="应用到">
        <el-radio-group v-model="addSlotForm.scope">
          <el-radio value="week">本周每天</el-radio>
          <el-radio value="day">仅所选日</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="默认开放">
        <el-switch v-model="addSlotForm.open" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="addSlotVisible = false">取消</el-button>
      <el-button type="primary" @click="confirmAddSlot">确定</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import request, { merchantApi } from '@/config/axios'
import {
  DEFAULT_STORE_SLOTS,
  formatSlot,
  normalizeWorkDate,
  parseSlot,
  slotKey,
  slotsOverlap,
  sortSlots,
  validateSlotRange
} from '@/views/o2o/utils/scheduleSlots'

const props = defineProps<{ merchant?: boolean }>()
const message = useMessage()
const visible = ref(false)
const saving = ref(false)
const store = ref<any>()
const selectedDate = ref('')
const weekStart = ref(dayjs().startOf('week').add(1, 'day').format('YYYY-MM-DD'))
const rows = ref<any[]>([])
const slotTemplates = ref<string[]>([...DEFAULT_STORE_SLOTS])
const serverSlotKeys = ref<string[]>([])
const addSlotVisible = ref(false)
const addSlotForm = reactive({
  start: '09:00',
  end: '10:00',
  scope: 'week' as 'week' | 'day',
  open: true
})

function getApi<T = any>(url: string, params?: any): Promise<T> {
  return props.merchant
    ? merchantApi.get<T>({ url: `/merchant-api${url}`, params })
    : request.get({ url, params })
}

function putApi(url: string, data: any) {
  return props.merchant
    ? merchantApi.put({ url: `/merchant-api${url}`, data })
    : request.put({ url, data })
}

function weekDates() {
  return Array.from({ length: 7 }).map((_, day) =>
    dayjs(weekStart.value).add(day, 'day').format('YYYY-MM-DD')
  )
}

function rebuildRows(preserve?: Map<string, any>) {
  const map = preserve || new Map(rows.value.map((r) => [slotKey(r.workDate, r.timeSlot), r]))
  rows.value = weekDates().flatMap((workDate) =>
    slotTemplates.value.map((timeSlot) => {
      const hit = map.get(slotKey(workDate, timeSlot))
      return hit
        ? {
            ...hit,
            workDate,
            timeSlot,
            capacity: Math.max(Number(hit.capacity) || 1, Number(hit.booked) || 0),
            booked: Number(hit.booked) || 0,
            open: Boolean(hit.open)
          }
        : { workDate, timeSlot, capacity: 1, booked: 0, open: false }
    })
  )
}

async function loadSchedule() {
  if (!store.value?.id) {
    slotTemplates.value = [...DEFAULT_STORE_SLOTS]
    rebuildRows(new Map())
    return
  }
  const listUrl = props.merchant ? '/o2o/schedule/store/list' : '/o2o/schedule/list'
  const list = await getApi<any[]>(listUrl, {
    storeId: store.value.id,
    providerId: 0,
    workDateFrom: weekStart.value,
    workDateTo: dayjs(weekStart.value).add(6, 'day').format('YYYY-MM-DD')
  })
  const normalized = (list || []).map((slot) => {
    const workDate = normalizeWorkDate(slot.workDate)
    const timeSlot = String(slot.timeSlot || '').trim()
    return {
      ...slot,
      workDate,
      timeSlot,
      capacity: Math.max(Number(slot.capacity) || 1, Number(slot.booked) || 0),
      booked: Number(slot.booked) || 0,
      open: slot.status !== 'CLOSED'
    }
  })
  serverSlotKeys.value = normalized.map((s) => slotKey(s.workDate, s.timeSlot))
  const fromDb = [...new Set(normalized.map((s) => s.timeSlot).filter(Boolean))]
  slotTemplates.value = sortSlots(fromDb.length ? fromDb : [...DEFAULT_STORE_SLOTS])
  const preserve = new Map<string, any>()
  normalized.forEach((slot) => {
    preserve.set(slotKey(slot.workDate, slot.timeSlot), slot)
  })
  rebuildRows(preserve)
  if (!selectedDate.value) selectedDate.value = weekStart.value
}

async function open(row: any) {
  store.value = row
  selectedDate.value = ''
  visible.value = true
  await loadSchedule()
}

function openAddSlot() {
  addSlotForm.start = '09:00'
  addSlotForm.end = '10:00'
  addSlotForm.scope = selectedDate.value ? 'day' : 'week'
  addSlotForm.open = true
  addSlotVisible.value = true
}

function confirmAddSlot() {
  const err = validateSlotRange(addSlotForm.start, addSlotForm.end)
  if (err) {
    message.warning(err)
    return
  }
  const timeSlot = formatSlot(addSlotForm.start, addSlotForm.end)
  if (slotTemplates.value.includes(timeSlot)) {
    message.warning('该时段已存在')
    return
  }
  slotTemplates.value = sortSlots([...slotTemplates.value, timeSlot])
  const dates =
    addSlotForm.scope === 'day' && selectedDate.value
      ? [selectedDate.value]
      : weekDates()
  const map = new Map(rows.value.map((r) => [slotKey(r.workDate, r.timeSlot), r]))
  dates.forEach((workDate) => {
    map.set(slotKey(workDate, timeSlot), {
      workDate,
      timeSlot,
      capacity: 1,
      booked: 0,
      open: addSlotForm.open
    })
  })
  rebuildRows(map)
  addSlotVisible.value = false
}

async function removeSlotTemplate(timeSlot: string) {
  const booked = rows.value
    .filter((r) => r.timeSlot === timeSlot)
    .reduce((sum, r) => sum + (Number(r.booked) || 0), 0)
  if (booked > 0) {
    message.warning(`时段 ${timeSlot} 已有预约，不能删除`)
    return
  }
  try {
    await message.confirm(`确认删除时段「${timeSlot}」？`)
  } catch {
    return
  }
  slotTemplates.value = slotTemplates.value.filter((s) => s !== timeSlot)
  rebuildRows()
}

function applyWorkWeek() {
  if (!slotTemplates.value.length) {
    message.warning('请先添加时段')
    return
  }
  rows.value.forEach((row) => {
    const weekday = dayjs(row.workDate).day()
    row.open = weekday !== 0 && weekday !== 6
    row.capacity = Math.max(row.booked, 1)
  })
}

function setDayOff() {
  if (!selectedDate.value) {
    message.warning('请先点选左侧某一天')
    return
  }
  rows.value
    .filter((row) => row.workDate === selectedDate.value)
    .forEach((row) => {
      row.open = false
    })
}

function copyNextWeek() {
  const nextStart = dayjs(weekStart.value).add(7, 'day').format('YYYY-MM-DD')
  const map = new Map<string, any>()
  rows.value.forEach((row) => {
    const workDate = dayjs(normalizeWorkDate(row.workDate)).add(7, 'day').format('YYYY-MM-DD')
    map.set(slotKey(workDate, row.timeSlot), {
      workDate,
      timeSlot: row.timeSlot,
      capacity: Math.max(Number(row.capacity) || 1, 1),
      booked: 0,
      open: Boolean(row.open)
    })
  })
  weekStart.value = nextStart
  rebuildRows(map)
  selectedDate.value = nextStart
  message.success('已复制到下一周，请确认后保存')
}

async function save() {
  if (!store.value?.id) {
    message.warning('门店信息丢失，请关闭后重开排班')
    return
  }
  const openByDate = new Map<string, string[]>()
  for (const row of rows.value) {
    const workDate = normalizeWorkDate(row.workDate)
    const timeSlot = String(row.timeSlot || '').trim()
    if (!workDate || !parseSlot(timeSlot)) {
      message.error(`时段格式无效：${workDate || '?'} ${timeSlot || '(空)'}`)
      return
    }
    const capacity = Number(row.capacity)
    const booked = Number(row.booked) || 0
    if (!Number.isFinite(capacity) || capacity < booked) {
      message.error(`${workDate} ${timeSlot} 容量不能小于已约 ${booked}`)
      return
    }
    if (row.open) {
      const list = openByDate.get(workDate) || []
      list.push(timeSlot)
      openByDate.set(workDate, list)
    }
  }
  for (const [workDate, slots] of openByDate) {
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        if (slotsOverlap(slots[i], slots[j])) {
          message.error(`${workDate} 开放时段重叠：${slots[i]} 与 ${slots[j]}，请先调整`)
          return
        }
      }
    }
  }

  saving.value = true
  try {
    const slotMap = new Map<string, { workDate: string; timeSlot: string; capacity: number; status: string }>()
    rows.value.forEach((row) => {
      const workDate = normalizeWorkDate(row.workDate)
      const timeSlot = String(row.timeSlot || '').trim()
      const key = slotKey(workDate, timeSlot)
      slotMap.set(key, {
        workDate,
        timeSlot,
        capacity: Math.max(Number(row.capacity) || 1, Number(row.booked) || 0),
        status: row.open ? 'AVAILABLE' : 'CLOSED'
      })
    })
    serverSlotKeys.value.forEach((key) => {
      if (slotMap.has(key)) return
      const sep = key.indexOf('|')
      if (sep < 0) return
      const workDate = normalizeWorkDate(key.slice(0, sep))
      const timeSlot = key.slice(sep + 1).trim()
      if (!workDate || !parseSlot(timeSlot)) return
      slotMap.set(slotKey(workDate, timeSlot), {
        workDate,
        timeSlot,
        capacity: 1,
        status: 'CLOSED'
      })
    })
    const slots = [...slotMap.values()]
    if (!slots.length) {
      message.warning('没有可保存的排班数据')
      return
    }
    const batchUrl = props.merchant ? '/o2o/schedule/store/batch' : '/o2o/schedule/batch'
    await putApi(batchUrl, {
      storeId: store.value.id,
      providerId: 0,
      slots
    })
    message.success('门店排班已保存')
    visible.value = false
  } catch (e: any) {
    message.error(e?.msg || e?.message || '排班保存失败')
  } finally {
    saving.value = false
  }
}

defineExpose({ open })
</script>
